import { useToast } from '@/hooks/use-toast';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useAuthStore } from '@/state/store/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getToilEarnedSummary,
  getToilRequests,
  requestToil,
  withdrawToilRequest,
} from '../services/toil.service';
import { GetToilRequestsParams, ToilRequestWorkflowInput } from '../types/toil.types';

export const useCurrentUserId = () => useAuthStore((state) => state.user?.itemId ?? '');

export const useGetToilRequests = (params: GetToilRequestsParams, enabled = true) => {
  return useGlobalQuery({
    queryKey: ['hr-toil-requests', params],
    queryFn: () => getToilRequests(params),
    enabled,
    staleTime: 0,
  });
};

export const useGetToilEarnedSummary = (userId: string, periodStart?: string, periodEnd?: string) =>
  useGlobalQuery({
    queryKey: ['hr-toil-requests', 'earned-summary', userId, periodStart, periodEnd],
    queryFn: () => getToilEarnedSummary(userId, periodStart as string, periodEnd as string),
    enabled: Boolean(userId && periodStart && periodEnd),
    staleTime: 60 * 1000,
  });

export const useRequestToil = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: ToilRequestWorkflowInput) => requestToil(input),
    // The toast shows the TOIL_REQUEST workflow's own answer (e.g. "Already requested for that date").
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['hr-toil-requests'] });
      if (result.IsSuccessful) {
        toast({ title: result.Message || t('TOIL_REQUEST_SUBMITTED') });
      } else {
        toast({ title: result.Message || t('SOMETHING_WENT_WRONG'), variant: 'destructive' });
      }
    },
  });
};

export const useWithdrawToilRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => withdrawToilRequest(itemId),
    // The toast shows the TOIL_WITHDRAW workflow's own answer (e.g. "Only a pending TOIL request can be withdrawn").
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['hr-toil-requests'] });
      if (result.IsSuccessful) {
        toast({ title: t('TOIL_REQUEST_WITHDRAWN') });
      } else {
        toast({ title: result.Message || t('SOMETHING_WENT_WRONG'), variant: 'destructive' });
      }
    },
  });
};
